<script>
    import hosted from '../private/hosted';
    import $util from '../../global/util';

    const keyEsc = 27;

    let stack = [];
    let msave;

    /**
     * This is a dialog view that can be modeless or modal, and is free standing (not anchored to an element or the
     * window). Dialogs may be used inline in a template, but are rendered out of place to prevent clipping. It is
     * recommended that you encapsulate your dialog content in a component of its own. This is both to limit the scope
     * of a `render`, and because it is common to reuse components both within the context of a dialog and elsewhere.
     *
     * You can conditionalize the visibility of an `ib-dialog` using either `v-if` or `v-model`. Using `v-if`, the
     * dialog and its content are not instantiated until the dialog is made visible. Using `v-model`, the dialog and
     * its content are always instantiated but hidden when the mapped value is `false`. If you use `autoclose` together
     * with `v-if`, you will need to listen to the `input` event and set the mapped value manually.
     */
    export default {
        name: 'ib-dialog',

        mixins: [
            hosted
        ],

        inheritAttrs: false,

        props: {
            /**
             * If `true`, show this dialog. Generally mapped via `v-model`.
             */
            value: {
                type: Boolean,
                default: true
            },
            /**
             * If `true`, this is a modeless dialog, and will not block interaction with other
             * user interface elements.
             */
            modeless: Boolean,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String,
            /**
             * Shadow elevation (depth) [0-24].
             */
            elevation: Number,
            /**
             * Appearance modifier. `rounded` is supported.
             */
            appearance: String,
            /**
             * Manually set z-index; default z-index is 1050.
             */
            zIndex: Number,
            /**
             * If `true`, close this dialog when clicking anywhere outside of it.
             */
            autoclose: Boolean,
            /**
             * Accepts string of location keywords such as top, right, bottom, and left.
             * Combinations can be used to set the dialog in a corner such as "top right", "bottom right", "bottom left", or "top left"
             */
            location: String,
            /**
             * Add margin to dialog box.  Useful if setting location to add space around the dialog box.
             */
            margin: String
        },

        computed: {
            oclass() {
                const elevation = this.elevation;
                const appearance = this.appearance;
                return {
                    ['ib-el' + elevation]: elevation != null,
                    ['_dlgo-' + appearance]: appearance != null
                };
            },
            dynamicStyles() {
                let styles = {},
                    hasLocation = false;

                if (this.margin) {
                    styles.margin = this.margin;
                }

                if (this.location) {
                    if (this.location.includes('top') && !this.location.includes('bottom')) {
                        styles.top = 0
                        hasLocation = true
                    }
                    if (this.location.includes('right') && !this.location.includes('left')) {
                        styles.right = 0
                        hasLocation = true
                    }
                    if (this.location.includes('bottom') && !this.location.includes('top')) {
                        styles.bottom = 0
                        hasLocation = true
                    }
                    if (this.location.includes('left') && !this.location.includes('right')) {
                        styles.left = 0
                        hasLocation = true
                    }
                }

                if (hasLocation) {
                    styles.position = 'absolute';
                }

                return styles;
            }
        },

        watch: {
            modeless(modeless) {
                if (modeless) {
                    this.releaseModal();
                } else {
                    this.captureModal();
                }
            }
        },

        mounted() {
            if (this.ivalue) {
                this.capture();
            }
        },

        beforeDestroy() {
            if (this.ivalue) {
                this.release();
                this.$emit('input', false);
            }
        },

        render(h) {
            const vm = this;

            // Satisfy vue-docgen-api
            if (0) {
                /**
                 * @slot This slot accepts header buttons, or other content that appears at the top of the dialog. It is
                 * never scrolled out of view.
                 */
                const sheader = this.$slots.header;
                /**
                 * @slot This slot accepts footer buttons, or other content that appears at the bottom of the dialog. It is
                 * never scrolled out of view. Buttons within this slot may take on platform dependent custom styles.
                 */
                const sfooter = this.$slots.footer;
                /**
                 * @slot Main content. Will scroll on overflow.
                 */
                const sdefault = this.$slots.default;
            }

            const sheader = vm._t('header');
            const sfooter = vm._t('footer');
            const sdefault = vm._t('default');
            const modal = !vm.modeless;
            return h('transition', {
                'attrs': {
                    'name': '_tr-modal',
                    'appear': true
                }
            }, [
                h('div', {
                    'staticClass': '_dlg',
                    'class': {
                        '_dlg-modal': modal
                    },
                    'style': {
                        'zIndex': vm.zIndex
                    },
                    'attrs': {
                        'tabindex': 0
                    },
                    'directives': [
                        {
                            name: 'show',
                            rawName: 'v-show',
                            value: vm.ivalue,
                            expression: 'show'
                        }
                    ],
                    'on': {
                        keydown: vm.keydown,
                        ...vm.autoclose ? {
                            mousedown: vm.mousedown,
                            touchstart: vm.mousedown,
                        } : void 0
                    }
                }, [
                    h('div', {
                        'staticClass': vm.classAttrs('_dlgo'),
                        'class': vm.oclass,
                        'attrs': {
                            'role': 'dialog',
                            'aria-modal': modal,
                            ...vm.$attrs
                        },
                        'style': [{
                            width: vm.width,
                            height: vm.height,
                        },
                        vm.dynamicStyles
                        ]
                    }, [
                        sheader ? h('div', {
                            'staticClass': '_dlgh'
                        }, sheader) : vm._e(),
                        sdefault ? h('div', {
                            'staticClass': '_dlgi'
                        }, sdefault) : vm._e(),
                        sfooter ? h('div', {
                            'staticClass': '_dlgf'
                        }, sfooter) : vm._e()
                    ])
                ])
            ]);
        },

        methods: {
            getLocationValue(type) {
                return this.location.indexOf(type) ? '0px' : 'auto'
            },
            capture() {
                if (!this.modeless) {
                    this.captureModal();
                }
                setTimeout(this.setfocus, 96);
                const scroll = $util.scrollChild(this.proxy_.$el);
                if (scroll) {
                    scroll.scrollTop = 0;
                }
            },

            release() {
                if (!this.modeless) {
                    this.releaseModal();
                }
            },

            captureModal() {
                if (!stack.length) {
                    const body = document.body;
                    if (window.innerWidth > body.clientWidth) {
                        msave = {
                            x: window.pageXOffset,
                            y: window.pageYOffset
                        };
                        body.style.left = -msave.x + 'px';
                        body.style.top = -msave.y + 'px';
                        body.classList.add('_mscroll');
                    } else {
                        msave = void 0;
                        body.classList.add('_mfull');
                    }
                }
                stack.push(this);
                addEventListener('focusin', this.focusin, true);
            },

            releaseModal() {
                stack = stack.filter(dialog => dialog !== this);
                if (stack.length) {
                    stack[stack.length - 1].setfocus();
                } else {
                    const body = document.body;
                    if (msave) {
                        body.style.left = '';
                        body.style.top = '';
                        body.classList.remove('_mscroll');
                        scrollTo(msave.x, msave.y);
                        msave = void 0;
                    } else {
                        body.classList.remove('_mfull');
                    }
                }
                removeEventListener('focusin', this.focusin, true);
            },

            close(e) {
                this.release();
                this.$emit('input', this.ivalue = false);
                e.preventDefault();
            },

            /** Close dialog on backdrop click */
            mousedown(e) {
                if (e.which == (e.touches ? 0 : 1) && e.currentTarget === e.target) {
                    this.close(e);
                }
            },

            /** Close dialog on Escape key */
            keydown(e) {
                if (e.which == 27) {
                    this.close(e);
                }
            },

            /** Focus on the dialog, or a containing [autofocus] element */
            setfocus() {
                const dialog = this.proxy_.$el;
                (dialog.querySelector('[autofocus]') || dialog).focus();
            },

            /** Trap element focus to dialog, unless it is to some higher z-index overlay */
            focusin(e) {
                if (this === stack[stack.length - 1]) {
                    const target = e.target;
                    const dialog = this.proxy_.$el;
                    if (target !== document) {
                        let zIndex;
                        let el = target;
                        while (el) {
                            if (el === dialog) {
                                return;
                            }
                            const style = getComputedStyle(el);
                            if (style['position'] != 'static') {
                                const z = style['z-index'];
                                if (!isNaN(z)) {
                                    zIndex = z;
                                }
                            }
                            el = el.parentElement;
                        }
                        if (zIndex == null || zIndex < (this.zIndex || 1050)) {
                            const tabbableChildren = $util.tabbableChildren(dialog);
                            (tabbableChildren.length ? tabbableChildren[0].el : dialog).focus();
                        }
                    }
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    body._mscroll {
        position: fixed;
        min-width: 100%;
        min-height: 100%;
    }
    body._mfull {
        overflow: hidden;
    }
    body._mscroll,
    body._mfull {
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
    }

    ._dlg {
        position: fixed;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: @z-dialog;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        outline: none;
        pointer-events: none;

        &-modal {
            pointer-events: all;
            /** Backdrop */
            &::before {
                content: '';
                position: fixed;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: @bg-backdrop;
                pointer-events: none;
            }
        }

        &o {
            display: flex;
            flex-direction: column;
            position: relative;
            width: @modal-width;
            max-width: 95%;
            max-height: 95%;
            pointer-events: all;
            .dlg-outer();
            & when not (@dialog-elevation = none) {
                .elevation(@dialog-elevation);
            }
            > :first-child {
                border-top-left-radius: inherit;
                border-top-right-radius: inherit;
            }
            > :last-child {
                border-bottom-left-radius: inherit;
                border-bottom-right-radius: inherit;
            }
            &-rounded {
                .dlg-rounded();
            }
        }
        &h,
        &f {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            flex: 0 0 auto;
            overflow: hidden;
        }
        &h {
            .dlg-header();
        }
        &f {
            .dlg-footer();
        }
        &i {
            display: flex;
            flex: 1 1 auto;
            align-items: stretch;
            position: relative;
            overflow-x: hidden;
            overflow-y: auto;
            overscroll-behavior: contain;
            .dlg-main();
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            ib-button(@click="show = true") Show Dialog
            ib-dialog(v-model="show", id="ex-dialog")
                h1.ib-col.inset-16.text-center My Dialog
                template(v-slot:footer)
                    ib-button(@click="show = false") Dismiss
    </template>
    <script>
        {
            data() {
                return {
                    show: false
                };
            }
        }
    </script>
</ib-example>

<ib-example id="location" name="location">
    <template lang="pug">
        div
            ib-dropdown(:data="locations" v-model="selectedLocation")
            ib-button(@click="show = true") Show Dialog
            ib-dialog(v-model="show", id="ex-dialog", :location="selectedLocation", margin="20px", modeless)
                h1.ib-col.inset-16.text-center My Dialog
                template(v-slot:footer)
                    ib-button(@click="show = false") Dismiss
    </template>
    <script>
        {
            data() {
                return {
                    show: false,
                    locations: ['default', 'top', 'right', 'bottom', 'left', 'top right', 'bottom right', 'bottom left', 'top left'],
                    selectedLocation: ''
                };
            }
        }
    </script>
</ib-example>
